import React, { useEffect, useId, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronUp, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { addDocumentTypesWithRetry, classifyTaxonomyFailure } from '../lib/uploadTaxonomy';

const DOCUMENT_TYPE_PANEL_KEY = 'qflow:upload-document-types-open';

const mergeDocumentTypes = (current, incoming) => {
  const byValue = new Map(current.map(item => [item.value, item]));
  incoming.forEach(item => {
    const option = {
      ...item,
      value: item.value || `custom_${item.id}`,
      built_in: Boolean(item.built_in),
    };
    byValue.set(option.value, option);
  });
  return [...byValue.values()].sort((left, right) => {
    if (left.built_in !== right.built_in) return left.built_in ? -1 : 1;
    return left.name.localeCompare(right.name);
  });
};

const DocumentTypePicker = ({ value = '', onChange, compact = false }) => {
  const suggestionListId = `document-types-${useId().replace(/:/g, '')}`;
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [canManage, setCanManage] = useState(false);
  const [open, setOpen] = useState(() => {
    try {
      return window.sessionStorage.getItem(DOCUMENT_TYPE_PANEL_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [bulkNames, setBulkNames] = useState('');
  const [saving, setSaving] = useState(false);
  const [directName, setDirectName] = useState('');
  const [directDirty, setDirectDirty] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [editingName, setEditingName] = useState('');

  const load = async () => {
    const [typesResult, capabilityResult] = await Promise.allSettled([
      axiosInstance.get('/source-types'),
      axiosInstance.get('/upload-taxonomy/capabilities'),
    ]);
    if (typesResult.status === 'fulfilled') {
      const typesResponse = typesResult.value;
      const custom = (typesResponse.data || []).map(item => ({
        ...item,
        value: `custom_${item.id}`,
        built_in: false,
      }));
      setTypes(custom);
    }
    if (capabilityResult.status === 'fulfilled') {
      const capabilityResponse = capabilityResult.value;
      setCanManage(Boolean(capabilityResponse.data?.can_manage));
    }
    setLoadingTypes(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    try {
      window.sessionStorage.setItem(DOCUMENT_TYPE_PANEL_KEY, String(open));
    } catch {
      // Browsers with blocked storage can still use the picker for this session.
    }
  }, [open]);

  const selected = useMemo(() => types.find(item => item.value === value) || null, [types, value]);
  useEffect(() => {
    if (!directDirty) setDirectName(selected?.name || '');
  }, [selected?.name, directDirty]);

  const updateDirectName = (nextName) => {
    setDirectName(nextName);
    const existing = types.find(item => item.name.trim().toLocaleLowerCase() === nextName.trim().toLocaleLowerCase());
    if (existing) {
      setDirectDirty(false);
      onChange?.(existing.value, existing);
    } else {
      setDirectDirty(true);
      onChange?.('', nextName.trim() ? { name: nextName, pending: true } : null);
    }
  };

  const handleFailure = (error, fallback = 'Could not add document type') => {
    const failure = classifyTaxonomyFailure(error, fallback);
    toast.error(failure.message);
    if (failure.sessionRevoked) {
      localStorage.removeItem('token');
      window.setTimeout(() => window.location.assign('/auth'), 900);
    }
  };

  const commitDirectName = async () => {
    const name = directName.trim().replace(/\s+/g, ' ');
    if (!name || saving) {
      if (!name) onChange?.('', null);
      return;
    }
    const existing = types.find(item => item.name.trim().toLocaleLowerCase() === name.toLocaleLowerCase());
    if (existing) {
      setDirectName(existing.name);
      setDirectDirty(false);
      onChange?.(existing.value, existing);
      return;
    }
    if (!canManage) {
      toast.error('Choose one of your organisation’s saved document types');
      setDirectName(selected?.name || '');
      return;
    }
    setSaving(true);
    try {
      const response = await addDocumentTypesWithRetry(axiosInstance, name);
      const added = response.data?.document_types || [];
      const item = added[0];
      if (!item) throw new Error('Document type was not returned after saving');
      const option = { ...item, value: `custom_${item.id}`, built_in: false };
      setTypes(current => mergeDocumentTypes(current, [option]));
      setDirectName(option.name);
      setDirectDirty(false);
      onChange?.(option.value, option);
      toast.success(`Document type “${option.name}” ready`);
    } catch (error) {
      handleFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const addMany = async () => {
    if (!bulkNames.trim() || saving) return;
    setSaving(true);
    try {
      const response = await addDocumentTypesWithRetry(axiosInstance, bulkNames);
      const added = response.data?.document_types || [];
      setTypes(current => mergeDocumentTypes(current, added));
      setBulkNames('');
      if (added.length) {
        const option = { ...added[0], value: `custom_${added[0].id}`, built_in: false };
        setDirectName(option.name);
        setDirectDirty(false);
        onChange?.(option.value, option);
      }
      setOpen(true);
      toast.success(`${added.length} document type${added.length === 1 ? '' : 's'} ready`);
    } catch (error) {
      handleFailure(error, 'Could not add document types');
    } finally {
      setSaving(false);
    }
  };

  const saveRename = async (item) => {
    if (!editingName.trim() || saving) return;
    setSaving(true);
    try {
      const response = await axiosInstance.put(`/source-types/${item.id}`, { name: editingName.trim() });
      setTypes(current => current.map(type => type.id === item.id ? { ...type, ...response.data } : type));
      setEditingId('');
      setEditingName('');
      toast.success('Document type updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update document type');
    } finally {
      setSaving(false);
    }
  };

  const archive = async (item) => {
    try {
      await axiosInstance.delete(`/source-types/${item.id}`);
      setTypes(current => current.filter(type => type.id !== item.id));
      if (value === item.value) {
        setDirectName('');
        setDirectDirty(false);
        onChange?.('', null);
      }
      toast.success('Document type archived');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not archive document type');
    }
  };

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'} data-testid="document-type-picker">
      <div>
        <Label className="text-xs">Document type</Label>
        <div className="mt-1 flex gap-2">
          <div className="relative min-w-0 flex-1">
            <Input
              value={directName}
              onChange={event => updateDirectName(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitDirectName();
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  setDirectName('');
                  setDirectDirty(false);
                  onChange?.('', null);
                }
              }}
              list={suggestionListId}
              placeholder={loadingTypes ? 'Loading saved document types…' : 'Type a document type'}
              disabled={loadingTypes || saving}
              maxLength={100}
              className="h-10 bg-white pr-9"
              aria-label="Document type"
              aria-describedby={`${suggestionListId}-help`}
            />
            {saving && <Loader2 size={15} className="absolute right-3 top-3 animate-spin text-slate-400" aria-hidden="true" />}
          </div>
          <Button type="button" variant="outline" className="h-10 shrink-0" onClick={commitDirectName} disabled={!directName.trim() || saving || loadingTypes}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Save
          </Button>
        </div>
        <datalist id={suggestionListId}>
          {types.map(item => <option key={item.value} value={item.name} />)}
        </datalist>
        <p id={`${suggestionListId}-help`} className="mt-1 text-[11px] text-slate-500">
          {canManage ? 'Type a new name and press Enter or Save, or choose a previously saved type.' : 'Choose an organisation-saved document type.'}
        </p>
      </div>

      {canManage && (
        <div className="rounded-lg border border-slate-200 bg-slate-50">
          <button type="button" className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium text-slate-700" onClick={() => setOpen(current => !current)} aria-expanded={open}>
            <span>Manage your organisation’s document types</span>
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {open && (
            <div className="space-y-3 border-t border-slate-200 p-3">
              <div>
                <Label className="text-xs">Paste one or many types</Label>
                <Textarea value={bulkNames} onChange={event => setBulkNames(event.target.value)} placeholder={'Advice guide\nReference guide\nCalculation guide'} className="mt-1 min-h-20 bg-white" />
                <p className="mt-1 text-[11px] text-slate-500">Separate values with a new line, comma, semicolon or spreadsheet cells.</p>
                <Button type="button" size="sm" className="mt-2" onClick={addMany} disabled={!bulkNames.trim() || saving}>
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add types
                </Button>
              </div>
              <div className="space-y-1">
                {types.filter(item => !item.built_in).map(item => (
                  <div key={item.id} className="flex items-center gap-2 rounded-md bg-white px-2 py-1.5 text-xs">
                    {editingId === item.id ? (
                      <>
                        <Input value={editingName} onChange={event => setEditingName(event.target.value)} className="h-8 flex-1" maxLength={100} />
                        <Button type="button" size="icon" variant="ghost" aria-label={`Save ${item.name}`} onClick={() => saveRename(item)}><Check size={14} /></Button>
                      </>
                    ) : (
                      <>
                        <span className="min-w-0 flex-1 truncate">{item.name}</span>
                        <Button type="button" size="icon" variant="ghost" aria-label={`Edit ${item.name}`} onClick={() => { setEditingId(item.id); setEditingName(item.name); }}><Pencil size={13} /></Button>
                        <Button type="button" size="icon" variant="ghost" aria-label={`Archive ${item.name}`} onClick={() => archive(item)}><Trash2 size={13} /></Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentTypePicker;
